// src/components/Layout/AppShell.jsx
'use client'

import { useEffect, useState, useMemo } from 'react'
import { Outlet } from 'react-router-dom'
import { useAuth, isEmailAllowed, BETA_GATE_ENABLED } from '@/context/AuthContext'
import { useTeam } from '@/context/TeamContext'
import { useRole } from '@/context/RoleContext'
import { SidebarLayout } from '@/components/sidebar-layout'
import Sidebar from '@/components/sidebar'
import Loader from '@/components/Loader'
import WeatherAlertBanner from '@/components/WeatherAlertBanner'
import BetaWelcomeModal from '@/components/beta/BetaWelcomeModal'
import BetaFeedbackButton from '@/components/beta/BetaFeedbackButton'

export default function AppShell() {
  const { loading, firebaseUser, userProfile } = useAuth()
  const { teams } = useTeam()
  const { parentTeamIds } = useRole()

  const appBg = 'min-h-svh bg-surface-light text-neutral-900 dark:text-neutral-100'

  // Aggregate team ids for the global weather banner: every team the
  // user is on (coach + parent). Means a parent on Team A and a coach
  // on Team B sees alerts for both, regardless of which page they're
  // viewing. Sorted + de-duped inside the banner so reference stability
  // isn't a concern here.
  const allTeamIds = useMemo(() => {
    const ids = new Set();
    (teams || []).forEach((t) => t?.id && ids.add(t.id));
    (parentTeamIds || new Set()).forEach((id) => id && ids.add(id));
    return Array.from(ids);
  }, [teams, parentTeamIds]);

  // ── Beta welcome modal state ──────────────────────────────────────
  // Show automatically on first sign-in for any allowlisted user who
  // hasn't seen it yet. Dismissal writes `betaWelcomeSeenAt` to the
  // user doc so it never auto-shows again. The feedback button can
  // re-open it via `onShowWelcome`.
  const [welcomeOpen, setWelcomeOpen] = useState(false)

  useEffect(() => {
    if (!BETA_GATE_ENABLED) return
    if (!firebaseUser?.email || !userProfile) return
    if (!isEmailAllowed(firebaseUser.email)) return
    // Only auto-open the first time. `betaWelcomeSeenAt` is the
    // canonical "I've seen this" stamp on the user doc.
    if (!userProfile.betaWelcomeSeenAt) {
      setWelcomeOpen(true)
    }
  }, [firebaseUser?.email, userProfile?.betaWelcomeSeenAt, userProfile])

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${appBg}`}>
        <Loader />
      </div>
    )
  }

  // First-name fallback chain for the welcome greeting.
  const firstName =
    userProfile?.firstName ||
    (userProfile?.displayName || firebaseUser?.displayName || '').split(' ')[0] ||
    ''

  // Only show beta UI to allowlisted users. Anyone signed in but not
  // allowlisted is being force-logged-out by AuthContext anyway, but
  // belt-and-suspenders this stays hidden.
  const showBetaUi = BETA_GATE_ENABLED && isEmailAllowed(firebaseUser?.email)

  return (
    <div className={appBg}>
      <SidebarLayout sidebar={<Sidebar />}>
        {/* Global weather alerts. Pinned above page content so a coach
            sees an active alert no matter which page they're on. The
            banner renders null when there are no pending alerts, so
            non-alert days have zero visual cost. */}
        {allTeamIds.length > 0 && (
          <div className="px-4 sm:px-6 lg:px-8 pt-4">
            <WeatherAlertBanner teamIds={allTeamIds} />
          </div>
        )}
        <Outlet />
      </SidebarLayout>

      {showBetaUi && (
        <>
          <BetaWelcomeModal
            open={welcomeOpen}
            onClose={() => setWelcomeOpen(false)}
            firebaseUser={firebaseUser}
            firstName={firstName}
            onOpenFeedback={() => {
              // Welcome → "Send Kurt a note" — close welcome, then
              // signal the floating button to open. We use a custom
              // event because the button manages its own open state
              // and lives as a sibling, not a child.
              window.dispatchEvent(new CustomEvent('scrimly:openBetaFeedback'))
            }}
          />
          <BetaFeedbackButton
            firebaseUser={firebaseUser}
            userProfile={userProfile}
            onShowWelcome={() => setWelcomeOpen(true)}
          />
        </>
      )}
    </div>
  )
}
